/* Hallmark · engineering-workbench · component-scope
 * project tokens · pre-emit critique: P4 H4 E4 S5 R4 V4
 * Physical floor, overhead pathways and collision-aware address labels.
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useId } from 'react'
import { Plus, Minus, Scan, LocateFixed, Layers, Grid3X3 } from 'lucide-react'
import { RackMeta, RoutePoint, DcMeta, rackLabel } from '../../hooks/useCalculatorLogic'
import { VIEW_PPM, rackScreenRect, traySegments, rotatedRectBounds, parseAisleZones } from '../../utils/floorPlan.logic'
import { DEFAULT_FLOOR_PLAN } from '../../utils/floorPlanGeometry.logic'
import { calculatorMapScaleLabel } from '../../utils/calculatorMapScale.logic'
import { placeCalculatorLabels } from '../../utils/calculatorLabels.logic'
import { getRackTemperature, getThermalColor } from '../../utils/thermalOverlay.logic'
import { buildFloorPlanProjection, projectionBounds } from '../../utils/floorPlanProjection.logic'
import { rackIsVisible, semanticZoomLevel } from '../../utils/semanticZoom.logic'
import type { CalculationResponse } from '../../types'
import { FloorPlanningLayer, FloorReservationsList } from '../editor/FloorPlanningLayer'
import { placementFromSource } from '../../utils/placement.logic'
import type { FloorPlanning } from '../../utils/placement.logic'
import type { WorkOrderFloorOverlay } from '../../types/workOrderFloor'

interface TooltipState { x: number; y: number; rack: RackMeta; lbl: string }
interface FloorViewState {
  tr: { zoom: number; panX: number; panY: number }
  layers: { addresses: boolean; floor: boolean; pathways: boolean }
  showPlanning: boolean
  focusedRackId: number | null
}

function readFloorView(key?: string): FloorViewState | null {
  if (!key) return null
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null')
    if (!value || !Number.isFinite(value.tr?.zoom) || value.tr.zoom < 0.08 || value.tr.zoom > 10 ||
      !Number.isFinite(value.tr.panX) || !Number.isFinite(value.tr.panY) ||
      !['addresses', 'floor', 'pathways'].every(name => typeof value.layers?.[name] === 'boolean') ||
      typeof value.showPlanning !== 'boolean' || (value.focusedRackId !== null && !Number.isInteger(value.focusedRackId))) return null
    return value
  } catch { return null }
}

export function DcMapSVG({
  racks,
  route,
  sharedSegments = [],
  referenceRoute,
  routeSegments,
  routeWorkbench = false,
  sourceU,
  targetU,
  srcId,
  tgtId,
  onRackClick,
  dc,
  totalLength,
  aisleZones,
  focusedRackId,
  onEmptyClick,
  telemetryMap,
  workOrderOverlay,
  viewStateKey,
  inspectorOpen = false,
}: {
  racks: RackMeta[]
  route: RoutePoint[]
  sharedSegments?: CalculationResponse['route_segments']
  referenceRoute?: RoutePoint[]
  routeSegments?: CalculationResponse['route_segments']
  routeWorkbench?: boolean
  sourceU?: number
  targetU?: number
  srcId?: number
  tgtId?: number
  onRackClick?: (r: RackMeta) => void
  dc?: DcMeta
  totalLength?: number
  aisleZones?: unknown
  focusedRackId?: number | null
  onEmptyClick?: () => void
  telemetryMap?: Record<string | number, any> | Array<any> | null
  workOrderOverlay?: WorkOrderFloorOverlay | null
  viewStateKey?: string
  inspectorOpen?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [restoredView] = useState(() => readFloorView(viewStateKey))
  const initialViewReady = useRef(!!restoredView)
  const lastFocus = useRef<number | null | undefined>(restoredView?.focusedRackId)
  const [tr, setTr] = useState(restoredView?.tr ?? { zoom: 1, panX: 60, panY: 40 })
  const [isDragging, setIsDragging] = useState(false)
  const [flashId, setFlashId] = useState<number | null>(null)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const [layersOpen, setLayersOpen] = useState(false)
  const [showPlanning, setShowPlanning] = useState(restoredView?.showPlanning ?? false)
  const planning = (dc?.cable_trays_json as { planning?: FloorPlanning } | undefined)?.planning ?? { placements: [], rack_reservations: [] }
  const [layers, setLayers] = useState(restoredView?.layers ?? { addresses: true, floor: true, pathways: true })
  useEffect(() => {
    if (!viewStateKey) return
    try { sessionStorage.setItem(viewStateKey, JSON.stringify({ tr, layers, showPlanning, focusedRackId: focusedRackId ?? null })) }
    catch { /* Storage may be disabled; navigation still works without persisted presentation. */ }
  }, [viewStateKey, tr, layers, showPlanning, focusedRackId])
  const layerControlsRef = useRef<HTMLDivElement>(null)
  const floorGridId = useId()
  const dragRef = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null)

  const rawAisleZones = aisleZones ?? dc?.aisle_zones_json ?? (dc?.cable_trays_json as any)?.aisle_zones
  const aisleRects = useMemo(
    () => parseAisleZones(rawAisleZones, VIEW_PPM),
    [rawAisleZones],
  )

  const trays = useMemo(() => traySegments(dc?.cable_trays_json, VIEW_PPM), [dc?.cable_trays_json])

  const projection = useMemo(() => buildFloorPlanProjection({
    floorPlan: dc?.floor_plan_json ?? DEFAULT_FLOOR_PLAN,
    racks,
    aisleZones: Array.isArray(rawAisleZones) ? rawAisleZones : undefined,
    pathways: dc?.cable_trays_json as any,
  }), [dc?.floor_plan_json, dc?.cable_trays_json, rawAisleZones, racks])
  const floor = projection.floorPlan
  const tilePx = floor.tileSizeMm / 1000 * VIEW_PPM
  const floorX = floor.originXMm / 1000 * VIEW_PPM, floorY = floor.originYMm / 1000 * VIEW_PPM
  const scaleM = [20, 10, 5, 2, 1, 0.5, 0.2, 0.1].find(value => value * VIEW_PPM * tr.zoom <= 90) ?? 0.1
  const rects = useMemo(
    () => {
      const projectedById = new Map(projection.racks.map(candidate => [candidate.id, candidate]))
      return racks.map(r => {
      const projected = projectedById.get(String(r.id))
      const renderRack = projected ? {
        ...r,
        x_m: projected.placement.xM,
        y_m: projected.placement.yM,
        width_m: projected.placement.widthM,
        depth_m: projected.placement.depthM,
        rotation: projected.placement.orientation,
      } : r
      return { rack: r, ...rackScreenRect(renderRack, VIEW_PPM) }
      })
    },
    [racks, projection],
  )

  const dcBounds = useMemo(() => {
    const floorBounds = projectionBounds(projection)
    let minX = floorBounds.minXM * VIEW_PPM, minY = floorBounds.minYM * VIEW_PPM
    let maxX = floorBounds.maxXM * VIEW_PPM, maxY = floorBounds.maxYM * VIEW_PPM
    for (const r of rects) {
      const b = rotatedRectBounds(r)
      minX = Math.min(minX, b.minX)
      minY = Math.min(minY, b.minY)
      maxX = Math.max(maxX, b.maxX)
      maxY = Math.max(maxY, b.maxY)
    }
    for (const s of trays) {
      maxX = Math.max(maxX, s.x1, s.x2)
      maxY = Math.max(maxY, s.y1, s.y2)
      minX = Math.min(minX, s.x1, s.x2)
      minY = Math.min(minY, s.y1, s.y2)
    }
    for (const z of aisleRects) {
      if (!Number.isFinite(z.x) || !Number.isFinite(z.y) || !Number.isFinite(z.w) || !Number.isFinite(z.h)) continue
      minX = Math.min(minX, z.x)
      minY = Math.min(minY, z.y)
      maxX = Math.max(maxX, z.x + z.w)
      maxY = Math.max(maxY, z.y + z.h)
    }
    const ox = Math.min(0, minX)
    const oy = Math.min(0, minY)
    return { w: Math.max(maxX - ox + 80, 400), h: Math.max(maxY - oy + 80, 300), ox, oy }
  }, [projection, rects, trays, aisleRects])

  const dcW = dcBounds.w
  const dcH = dcBounds.h
  const zoomLevel = semanticZoomLevel(60 * tr.zoom)
  const contentBounds = useMemo(() => {
    if (rects.length === 0) return { minX: dcBounds.ox, minY: dcBounds.oy, maxX: dcBounds.ox + dcW, maxY: dcBounds.oy + dcH }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    rects.forEach(rect => {
      const bounds = rotatedRectBounds(rect)
      minX = Math.min(minX, bounds.minX)
      minY = Math.min(minY, bounds.minY)
      maxX = Math.max(maxX, bounds.maxX)
      maxY = Math.max(maxY, bounds.maxY)
    })
    trays.forEach(segment => {
      minX = Math.min(minX, segment.x1, segment.x2)
      minY = Math.min(minY, segment.y1, segment.y2)
      maxX = Math.max(maxX, segment.x1, segment.x2)
      maxY = Math.max(maxY, segment.y1, segment.y2)
    })
    return { minX, minY, maxX, maxY }
  }, [rects, trays, dcBounds.ox, dcBounds.oy, dcW, dcH])
  const visibleRects = useMemo(() => {
    if (!viewportSize.width || !viewportSize.height) return rects
    const margin = 80 / tr.zoom
    const viewport = {
      x: dcBounds.ox - tr.panX / tr.zoom - margin,
      y: dcBounds.oy - tr.panY / tr.zoom - margin,
      w: viewportSize.width / tr.zoom + margin * 2,
      h: viewportSize.height / tr.zoom + margin * 2,
    }
    return rects.filter(rect => {
      const bounds = rotatedRectBounds(rect)
      return rackIsVisible({ x: bounds.minX, y: bounds.minY, w: bounds.maxX - bounds.minX, h: bounds.maxY - bounds.minY }, viewport,
        rect.rack.id === focusedRackId || rect.rack.id === srcId || rect.rack.id === tgtId)
    })
  }, [rects, viewportSize, tr, dcBounds.ox, dcBounds.oy, focusedRackId, srcId, tgtId])
  const focusedRect = focusedRackId == null ? undefined : rects.find(rect => rect.rack.id === focusedRackId)

  const addressLabels = useMemo(() => {
    if (!routeWorkbench || !layers.addresses) return []
    const screenX = (x: number) => tr.panX + (x - dcBounds.ox) * tr.zoom
    const screenY = (y: number) => tr.panY + (y - dcBounds.oy) * tr.zoom
    const segment = (x1: number, y1: number, x2: number, y2: number) => ({ x1: screenX(x1), y1: screenY(y1), x2: screenX(x2), y2: screenY(y2) })
    const obstacles = (layers.pathways ? trays : []).map(s => segment(s.x1, s.y1, s.x2, s.y2))
    for (const points of [route, referenceRoute ?? []]) points.slice(1).forEach((p, i) => {
      obstacles.push(segment(points[i].x * VIEW_PPM, points[i].y * VIEW_PPM, p.x * VIEW_PPM, p.y * VIEW_PPM))
    })
    for (const s of [...(routeSegments ?? []), ...sharedSegments]) {
      if (s.from && s.to) obstacles.push(segment(s.from[0] * VIEW_PPM, s.from[1] * VIEW_PPM, s.to[0] * VIEW_PPM, s.to[1] * VIEW_PPM))
    }
    const candidates = visibleRects.map(rect => {
      const bounds = rotatedRectBounds(rect), id = rect.rack.id
      const address = rackLabel(rect.rack, dc)
      const endpoints = [id === srcId ? `A ${address} · U${sourceU ?? '—'}` : '', id === tgtId ? `B ${address} · U${targetU ?? '—'}` : ''].filter(Boolean)
      const label = endpoints.length ? endpoints.join(' / ') : address
      return { id, x: screenX(bounds.minX), y: screenY(bounds.minY), w: (bounds.maxX - bounds.minX) * tr.zoom,
        h: (bounds.maxY - bounds.minY) * tr.zoom, labelWidth: label.length * 7.5 + 12, labelHeight: 22,
        showLabel: endpoints.length > 0 || (bounds.maxX - bounds.minX) * tr.zoom >= 24,
        priority: endpoints.length > 0, label, endpoint: id === srcId ? 'A' : id === tgtId ? 'B' : undefined,
        bothEndpoints: id === srcId && id === tgtId }
    })
    const placed = placeCalculatorLabels(candidates, obstacles,
      { x: 8, y: 60, w: Math.max(0, viewportSize.width - 16), h: Math.max(0, viewportSize.height - 118) },
      [{ x: viewportSize.width - 68, y: viewportSize.height - 284, w: 68, h: 284 }])
    return placed.map(position => ({ ...candidates.find(r => r.id === position.id)!, ...position }))
  }, [routeWorkbench, layers, tr, dcBounds.ox, dcBounds.oy, trays, route, referenceRoute, routeSegments, sharedSegments,
    visibleRects, dc, srcId, tgtId, sourceU, targetU, viewportSize])

  useEffect(() => {
    if (!layersOpen) return
    const dismiss = (event: PointerEvent) => { if (!layerControlsRef.current?.contains(event.target as Node)) setLayersOpen(false) }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setLayersOpen(false); layerControlsRef.current?.querySelector('button')?.focus() }
    }
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', escape) }
  }, [layersOpen])

  const fitView = useCallback((mode: boolean | 'work-order' = false) => {
    if (!containerRef.current || racks.length === 0) return
    const { width, height } = containerRef.current.getBoundingClientRect()
    setViewportSize({ width, height })
    if (!width || !height) return
    const selectedRects = rects.filter(rect => rect.rack.id === srcId || rect.rack.id === tgtId)
    const workOrderPoints = mode === 'work-order' && workOrderOverlay ? [
      ...workOrderOverlay.routes.filter(item => item.state === 'available').flatMap(item => item.route.map(point => ({ x: point.x * VIEW_PPM, y: point.y * VIEW_PPM }))),
      ...workOrderOverlay.moves.flatMap(move => {
        const source = rects.find(rect => rect.rack.id === move.rack_id)
        if (!source) return []
        const bounds = [rotatedRectBounds(source)]
        if (move.target && !move.conflict) bounds.push(rotatedRectBounds({ ...source,
          x: move.target.x_m * VIEW_PPM, y: move.target.y_m * VIEW_PPM, rotation: move.target.rotation }))
        return bounds.flatMap(bound => [{ x: bound.minX, y: bound.minY }, { x: bound.maxX, y: bound.maxY }])
      }),
    ] : []
    const routePoints = mode === 'work-order' ? workOrderPoints : [...route.map(point => ({ x: point.x * VIEW_PPM, y: point.y * VIEW_PPM })),
      ...selectedRects.flatMap(rect => {
        const bounds = rotatedRectBounds(rect)
        return [{ x: bounds.minX, y: bounds.minY }, { x: bounds.maxX, y: bounds.maxY }]
      })]
    const bounds = mode && routePoints.length ? {
      minX: Math.min(...routePoints.map(point => point.x)), minY: Math.min(...routePoints.map(point => point.y)),
      maxX: Math.max(...routePoints.map(point => point.x)), maxY: Math.max(...routePoints.map(point => point.y)),
    } : contentBounds
    const PAD = mode === 'work-order' ? 120 : routeWorkbench ? 72 : 32
    const contentW = Math.max(bounds.maxX - bounds.minX, 1)
    const contentH = Math.max(bounds.maxY - bounds.minY, 1)
    const zx = (width - PAD * 2) / contentW
    const zy = (height - PAD * 2) / contentH
    const zoom = Math.max(0.08, Math.min(zx, zy, routeWorkbench ? 4 : 2.5))
    const centerX = (bounds.minX + bounds.maxX) / 2 - dcBounds.ox
    const centerY = (bounds.minY + bounds.maxY) / 2 - dcBounds.oy
    setTr({ zoom, panX: width / 2 - centerX * zoom, panY: height / 2 - centerY * zoom })
  }, [racks.length, contentBounds, dcBounds.ox, dcBounds.oy, routeWorkbench, route, rects, srcId, tgtId, workOrderOverlay])

  const fitViewRef = useRef(fitView)
  fitViewRef.current = fitView
  useLayoutEffect(() => {
    if (!viewStateKey || initialViewReady.current) return
    initialViewReady.current = true
    if (focusedRackId == null) fitViewRef.current()
  }, [viewStateKey, contentBounds, dc?.code])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const { width, height } = el.getBoundingClientRect()
      setViewportSize({ width, height })
      if (!viewStateKey) fitViewRef.current()
    }
    measure()
    const t = setTimeout(() => { if (!viewStateKey) fitViewRef.current() }, 30)
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => { clearTimeout(t); ro.disconnect() }
  }, [contentBounds, dc?.code, viewStateKey, restoredView])

  useEffect(() => { if (srcId != null) { setFlashId(srcId); setTimeout(() => setFlashId(null), 900) } }, [srcId])
  useEffect(() => { if (tgtId != null) { setFlashId(tgtId); setTimeout(() => setFlashId(null), 900) } }, [tgtId])

  // flyTo animation when a rack is focused (search, command palette, or direct link)
  useEffect(() => {
    if (viewStateKey && lastFocus.current === (focusedRackId ?? null)) return
    lastFocus.current = focusedRackId ?? null
    if (focusedRackId == null || !containerRef.current) return
    const target = rects.find(r => r.rack.id === focusedRackId)
    if (!target) return

    setFlashId(focusedRackId)
    const timer = setTimeout(() => setFlashId(null), 1200)

    const rect = containerRef.current.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const targetX = (target.x + target.w / 2) - dcBounds.ox
    const targetY = (target.y + target.h / 2) - dcBounds.oy
    const targetZoom = Math.max(1.5, Math.min(Math.max(tr.zoom, 1.8), 3.0))
    const offsetX = rect.width > 768 ? (rect.width - 380) / 2 : rect.width / 2
    const offsetY = rect.height / 2

    setTr({
      zoom: targetZoom,
      panX: offsetX - targetX * targetZoom,
      panY: offsetY - targetY * targetZoom,
    })

    return () => clearTimeout(timer)
  }, [focusedRackId, rects, dcBounds.ox, dcBounds.oy])

  const onMD = useCallback((e: React.MouseEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY, px: tr.panX, py: tr.panY, moved: false }
    setIsDragging(true)
  }, [tr.panX, tr.panY])
  const onMM = useCallback((e: React.MouseEvent) => {
    if (!dragRef.current) return
    const dx = e.clientX - dragRef.current.x, dy = e.clientY - dragRef.current.y
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true
    setTr(t => ({ ...t, panX: dragRef.current!.px + dx, panY: dragRef.current!.py + dy }))
  }, [])
  const onMU = useCallback(() => {
    dragRef.current = null
    setIsDragging(false)
  }, [])

  const touchRef = useRef<{ x: number; y: number; px: number; py: number; dist?: number } | null>(null)

  const onTS = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const t = e.touches[0]
      touchRef.current = { x: t.clientX, y: t.clientY, px: tr.panX, py: tr.panY }
    } else if (e.touches.length === 2) {
      const t1 = e.touches[0], t2 = e.touches[1]
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
      const cx = (t1.clientX + t2.clientX) / 2
      const cy = (t1.clientY + t2.clientY) / 2
      touchRef.current = { x: cx, y: cy, px: tr.panX, py: tr.panY, dist }
    }
  }, [tr.panX, tr.panY])

  const onTM = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current) return
    if (e.touches.length === 1 && touchRef.current.dist == null) {
      const t = e.touches[0]
      const dx = t.clientX - touchRef.current.x
      const dy = t.clientY - touchRef.current.y
      setTr(prev => ({ ...prev, panX: touchRef.current!.px + dx, panY: touchRef.current!.py + dy }))
    } else if (e.touches.length === 2 && touchRef.current.dist != null) {
      const t1 = e.touches[0], t2 = e.touches[1]
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
      if (dist > 0 && touchRef.current.dist > 0) {
        const scale = dist / touchRef.current.dist
        const cx = (t1.clientX + t2.clientX) / 2
        const cy = (t1.clientY + t2.clientY) / 2

        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect()
          const fx = cx - rect.left, fy = cy - rect.top
          setTr(prev => {
            const nz = Math.max(0.08, Math.min(prev.zoom * scale, 10))
            const lx = (fx - prev.panX) / prev.zoom, ly = (fy - prev.panY) / prev.zoom
            return { zoom: nz, panX: fx - lx * nz, panY: fy - ly * nz }
          })
          touchRef.current.dist = dist
        }
      }
    }
  }, [])

  const onTE = useCallback(() => {
    touchRef.current = null
  }, [])

  const zoomAt = useCallback((fx: number, fy: number, factor: number) => {
    setTr(t => {
      const nz = Math.max(0.08, Math.min(t.zoom * factor, 10))
      const lx = (fx - t.panX) / t.zoom, ly = (fy - t.panY) / t.zoom
      return { zoom: nz, panX: fx - lx * nz, panY: fy - ly * nz }
    })
  }, [])
  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const rect = containerRef.current!.getBoundingClientRect()
    zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.15 : 1 / 1.15)
  }, [zoomAt])

  const cablePath = useMemo(() => {
    if (!route || route.length < 2) return null
    const pts = route.map(p => `${p.x * VIEW_PPM},${p.y * VIEW_PPM}`)
    return `M ${pts[0]} L ${pts.slice(1).join(' L ')}`
  }, [route])

  const badgePt = useMemo(() => {
    const srcRack = rects.find(r => r.rack.id === srcId)
    const tgtRack = rects.find(r => r.rack.id === tgtId)
    if (!srcRack || !tgtRack || !totalLength) return null
    return {
      x: (srcRack.x + srcRack.w / 2 + tgtRack.x + tgtRack.w / 2) / 2,
      y: Math.min(srcRack.y, tgtRack.y) - 16,
    }
  }, [totalLength, rects, srcId, tgtId])

  if (racks.length === 0) {
    return (
      <div ref={containerRef} className="w-full h-full flex flex-col items-center justify-center gap-2 text-xs text-text-muted">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
          <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
        </svg>
        <span>Нет стоек для отображения</span>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative overflow-hidden select-none${routeWorkbench ? ' calculator-map' : ''}`}
    >
      <svg width="100%" height="100%" data-semantic-level={zoomLevel} data-rendered-racks={visibleRects.length}
        data-testid={routeWorkbench ? 'route-plan' : undefined} aria-label={routeWorkbench ? 'Маршрут кабеля, вид сверху. Высоты и запас учитываются в расчёте длины.' : 'План зала'}
        onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU} onMouseLeave={onMU}
        onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE} onTouchCancel={onTE}
        onWheel={onWheel}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      >
        <defs>
          {routeWorkbench && <pattern id={floorGridId} data-testid="floor-tile-grid" x={floorX} y={floorY}
            width={tilePx} height={tilePx} patternUnits="userSpaceOnUse">
            <path d={`M ${tilePx} 0 H 0 V ${tilePx}`} fill="none" stroke="var(--route-grid)"
              strokeWidth={0.6 / tr.zoom} />
          </pattern>}
          <pattern id="dc-g-min" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke={routeWorkbench ? 'var(--route-grid)' : 'rgba(255,255,255,0.03)'} strokeWidth="0.5"/>
          </pattern>
          <pattern id="dc-g-maj" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke={routeWorkbench ? 'var(--route-grid)' : 'rgba(255,255,255,0.06)'} strokeWidth="0.5"/>
          </pattern>
          <pattern id="dc-containment-cold" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(14, 165, 233, 0.16)" strokeWidth="1.5"/>
          </pattern>
          <pattern id="dc-containment-hot" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(239, 68, 68, 0.16)" strokeWidth="1.5"/>
          </pattern>
          <filter id="dc-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <style>{`
            @keyframes dc-flow { from { stroke-dashoffset: 30; } to { stroke-dashoffset: 0; } }
            @keyframes dc-flash {
              0%,100% { opacity:1; }
              25% { opacity:0.15; fill:#F59E0B; }
              60% { fill:#F59E0B; opacity:0.9; }
            }
            @keyframes dc-route-in {
              from { stroke-dashoffset: 3000; opacity:0; }
              to   { stroke-dashoffset: 0;    opacity:1; }
            }
            .dc-cable-bg  { stroke:rgba(59,130,246,0.15); stroke-width:10; fill:none; stroke-linecap:round; }
            .dc-cable     { stroke:#60A5FA; stroke-width:2.5; stroke-dasharray:14 6; stroke-linecap:round;
                            animation: dc-flow 0.6s linear infinite; fill:none; filter:url(#dc-glow); }
            .dc-cable-in  { stroke-dasharray:3000; animation: dc-route-in 0.5s ease-out forwards; }
            .dc-rack      { transition: fill 0.1s, stroke 0.1s; }
            .dc-rack-flash { animation: dc-flash 0.9s ease-in-out; }
          `}</style>
        </defs>

        <rect width="100%" height="100%" fill={routeWorkbench ? 'var(--bg-app)' : 'var(--color-bg-app,#0B1120)'}
          onClick={() => { if (!dragRef.current?.moved) onEmptyClick?.() }}/>
        {!routeWorkbench && <><rect width="100%" height="100%" fill="url(#dc-g-maj)" style={{ pointerEvents: 'none' }}/>
        <rect width="100%" height="100%" fill="url(#dc-g-min)" style={{ pointerEvents: 'none' }}/></>}

        <g
          data-testid="floor-plan-transform"
          transform={`translate(${tr.panX},${tr.panY}) scale(${tr.zoom}) translate(${-dcBounds.ox},${-dcBounds.oy})`}
          style={{
            transition: routeWorkbench || isDragging || touchRef.current ? 'none' : 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
          }}
        >

          <g data-plan-layer={routeWorkbench ? 'floor' : undefined} pointerEvents="none">
          {routeWorkbench && <>
            <rect data-testid="floor-surface" x={floorX} y={floorY} width={floor.widthMm / 1000 * VIEW_PPM}
              height={floor.heightMm / 1000 * VIEW_PPM} fill="var(--route-floor)" stroke="var(--route-support)" strokeWidth={1 / tr.zoom} />
            {layers.floor && tilePx * tr.zoom >= 6 && <rect x={floorX} y={floorY} width={floor.widthMm / 1000 * VIEW_PPM}
              height={floor.heightMm / 1000 * VIEW_PPM} fill={`url(#${floorGridId})`} />}
          </>}
          <g className="dc-aisle-zones">
            {aisleRects.map(zone => {
              const isCold = zone.type === 'cold'
              const isIndeterminate = zone.type === 'indeterminate'
              const fill = isIndeterminate ? 'rgba(239, 68, 68, 0.16)' : isCold ? 'rgba(14, 165, 233, 0.12)' : 'rgba(249, 115, 22, 0.12)'
              const stroke = isIndeterminate ? 'rgba(248, 113, 113, 0.85)' : isCold ? 'rgba(14, 165, 233, 0.35)' : 'rgba(249, 115, 22, 0.35)'
              const textColor = isIndeterminate ? '#F87171' : isCold ? '#38BDF8' : '#FB923C'
              const strokeDasharray = zone.isContained ? '6,4' : undefined
              const strokeWidth = zone.isContained ? 1.5 : 1

              const isHoriz = zone.w >= zone.h
              const cx = zone.x + zone.w / 2
              const cy = zone.y + zone.h / 2
              const minDim = Math.min(zone.w, zone.h)
              const fontSize = Math.max(8, Math.min(11, minDim * 0.24))
              const showText = minDim * tr.zoom >= 10 && minDim >= 14

              return (
                <g key={zone.id} className="dc-aisle-zone" style={{ pointerEvents: 'none' }}>
                  <rect
                    x={zone.x}
                    y={zone.y}
                    width={zone.w}
                    height={zone.h}
                    rx={2}
                    fill={fill}
                  />
                  {zone.isContained && (
                    <rect
                      x={zone.x}
                      y={zone.y}
                      width={zone.w}
                      height={zone.h}
                      rx={2}
                      fill={isCold ? 'url(#dc-containment-cold)' : 'url(#dc-containment-hot)'}
                    />
                  )}
                  <rect
                    x={zone.x}
                    y={zone.y}
                    width={zone.w}
                    height={zone.h}
                    rx={2}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                  />
                  {showText && (
                    <g transform={isHoriz ? undefined : `rotate(-90,${cx},${cy})`}>
                      <text
                        x={cx}
                        y={cy + 1}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fontSize}
                        fontFamily="'JetBrains Mono', monospace"
                        fontWeight="600"
                        fill={textColor}
                        opacity={0.85}
                        letterSpacing="0.05em"
                      >
                        {zone.label}
                      </text>
                    </g>
                  )}
                </g>
              )
            })}
          </g>

          </g>

          {!routeWorkbench && trays.map((s, i) => (
            <line key={`tray${i}`}
              x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
              stroke={routeWorkbench ? 'var(--route-support)' : 'rgba(59,130,246,0.45)'} strokeWidth={routeWorkbench ? 5 : 4} strokeLinecap="round"
              vectorEffect={routeWorkbench ? 'non-scaling-stroke' : undefined}
              opacity={routeWorkbench ? 0.4 : 1}
              style={{ pointerEvents: 'none' }}/>
          ))}

          {!routeWorkbench && cablePath && (
            <g>
              <path d={cablePath} className="dc-cable-bg"/>
              <path d={cablePath} className="dc-cable dc-cable-in" key={cablePath}/>
            </g>
          )}

          {!routeWorkbench && sharedSegments.map((segment, index) => (
            <line key={`shared-${index}`} data-testid="shared-route-segment"
              x1={segment.from[0] * VIEW_PPM} y1={segment.from[1] * VIEW_PPM}
              x2={segment.to[0] * VIEW_PPM} y2={segment.to[1] * VIEW_PPM}
              stroke="#fbbf24" strokeWidth={5} strokeDasharray="8 5" pointerEvents="none">
              <title>Общий сегмент с основным: {segment.edge_id}</title>
            </line>
          ))}

          {!routeWorkbench && badgePt && totalLength && (
            <g transform={`translate(${badgePt.x},${badgePt.y})`}>
              <rect x={-40} y={-15} width={80} height={30} rx={4}
                fill="#0F172A" stroke="#3B82F6" strokeWidth={1.5} opacity={0.97}/>
              <text textAnchor="middle" dominantBaseline="central"
                fontSize="13" fontFamily="'JetBrains Mono',monospace"
                fill="#93C5FD" fontWeight="bold">
                {totalLength.toFixed(1)} м
              </text>
            </g>
          )}

          <g data-plan-layer={routeWorkbench ? 'racks' : undefined}>
          {visibleRects.map(({ rack, x, y, w, h, rotation }) => {
            const isSrc    = rack.id === srcId
            const isTgt    = rack.id === tgtId
            const isFlash  = rack.id === flashId
            const isFocused = rack.id === focusedRackId
            const isBlocked = rack.is_blocked
            const lbl = rackLabel(rack, dc)
            const cx = x + w / 2
            const cy = y + h / 2

            const temp = getRackTemperature(rack, telemetryMap)
            const thermalStyle = getThermalColor(temp)

            let fill   = thermalStyle ? thermalStyle.fill : routeWorkbench ? 'var(--route-rack)' : '#1A2744'
            let stroke = thermalStyle ? thermalStyle.stroke : routeWorkbench ? 'var(--route-support)' : '#2D3E5A'
            let sw     = thermalStyle ? 1.5 : 0.8
            if (isSrc)      { fill='rgba(245,158,11,0.25)';  stroke='#F59E0B'; sw=2.5 }
            else if (isTgt) { fill='rgba(16,185,129,0.25)';  stroke='#10B981'; sw=2.5 }
            else if (isFocused) { fill='rgba(56,189,248,0.3)'; stroke='#38BDF8'; sw=2.5 }
            else if (isBlocked) { fill='rgba(239,68,68,0.08)'; stroke='#EF4444'; sw=0.7 }
            if (routeWorkbench && !thermalStyle) {
              fill = 'var(--route-rack)'
              stroke = isSrc ? 'var(--route-source)' : isTgt ? 'var(--route-target)' : 'var(--route-support)'
            }

            return (
              <g key={rack.id}
                data-testid={`rack-svg-${rack.id}`}
                data-estimated={String(rack.width_m == null || rack.depth_m == null)}
                role={onRackClick && !isBlocked ? 'button' : undefined}
                tabIndex={onRackClick && !isBlocked ? 0 : undefined}
                aria-label={`${lbl}${isSrc ? ', источник A' : ''}${isTgt ? ', назначение B' : ''}${rack.width_m == null || rack.depth_m == null ? ', контур оценочный' : ''}`}
                onKeyDown={event => {
                  if (!isBlocked && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onRackClick?.(rack) }
                }}
                onClick={() => !isBlocked && !dragRef.current?.moved && onRackClick?.(rack)}
                onMouseEnter={e => {
                  const b = containerRef.current!.getBoundingClientRect()
                  setTooltip({ x: e.clientX - b.left + 16, y: e.clientY - b.top + 16, rack, lbl })
                }}
                onMouseMove={e => {
                  const b = containerRef.current!.getBoundingClientRect()
                  setTooltip(t => t ? { ...t, x: e.clientX - b.left + 16, y: e.clientY - b.top + 16 } : null)
                }}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: isBlocked ? 'not-allowed' : 'pointer' }}
              >
                <g transform={`rotate(${rotation},${cx},${cy})`}>
                <rect x={x} y={y} width={w} height={h} rx={2}
                  fill={fill} stroke={stroke} strokeWidth={sw}
                  strokeDasharray={rack.width_m == null || rack.depth_m == null ? '4 3' : undefined}
                  vectorEffect={routeWorkbench ? 'non-scaling-stroke' : undefined}
                  className={`dc-rack${isFlash && !routeWorkbench ? ' dc-rack-flash' : ''}`}/>
                {routeWorkbench && <line x1={x + w * 0.25} y1={y + h} x2={x + w * 0.75} y2={y + h}
                  stroke={stroke} strokeWidth={3} vectorEffect="non-scaling-stroke" pointerEvents="none"><title>Передняя сторона стойки</title></line>}
                {zoomLevel === 'close' && !routeWorkbench && <rect x={x+sw} y={y+sw} width={w-sw*2} height={Math.min(5, h * 0.08)} rx={1}
                  fill={isSrc ? '#F59E0B' : isTgt ? '#10B981' : 'rgba(255,255,255,0.08)'}
                  style={{ pointerEvents: 'none' }}/>}
                {!routeWorkbench && zoomLevel !== 'far' &&
                <g transform={`rotate(-90,${cx},${cy})`}
                  style={{ pointerEvents: 'none' }}>
                  <text x={cx} y={cy+1}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize={Math.max(8, Math.min(12, w * 0.35))} fontWeight="600"
                    fontFamily="var(--font-data)"
                    fill={routeWorkbench ? 'var(--text-primary)' : isSrc || isTgt ? '#fff' : 'rgba(255,255,255,0.85)'}>
                    {routeWorkbench || zoomLevel !== 'medium' ? lbl : lbl.split('/').pop()}
                  </text>
                </g>}
                {isBlocked && (
                  <>
                    <line x1={x+4} y1={y+4} x2={x+w-4} y2={y+h-4}
                      stroke="#EF4444" strokeWidth="1.5" opacity="0.5" style={{ pointerEvents:'none' }}/>
                    <line x1={x+w-4} y1={y+4} x2={x+4} y2={y+h-4}
                      stroke="#EF4444" strokeWidth="1.5" opacity="0.5" style={{ pointerEvents:'none' }}/>
                  </>
                )}
                </g>
              </g>
            )
          })}
          </g>
          {routeWorkbench && <g data-plan-layer="pathways" pointerEvents="none">
            {layers.pathways && trays.map((s, i) => <g key={i}>
              <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} className="dc-tray-halo" />
              <line data-testid={`tray-segment-${i}`} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} className="dc-tray-centre" />
            </g>)}
            {referenceRoute && referenceRoute.length > 1 && <path data-testid="reference-route" className="dc-route-reference"
              d={`M ${referenceRoute.map(point => `${point.x * VIEW_PPM},${point.y * VIEW_PPM}`).join(' L ')}`} />}
            {cablePath && <path d={cablePath} className="dc-route-halo" />}
            {cablePath && <path d={cablePath} className="dc-route-path" />}
            {routeSegments?.filter(segment => segment.kind === 'rack_entry').map((segment, index) => <g key={index}>
              <line x1={segment.from[0] * VIEW_PPM} y1={segment.from[1] * VIEW_PPM} x2={segment.to[0] * VIEW_PPM} y2={segment.to[1] * VIEW_PPM} className="dc-route-halo" />
              <line
              x1={segment.from[0] * VIEW_PPM} y1={segment.from[1] * VIEW_PPM} x2={segment.to[0] * VIEW_PPM} y2={segment.to[1] * VIEW_PPM}
              className="dc-route-path dc-route-entry" /></g>)}
            {sharedSegments.map((segment, index) => <line key={`shared-${index}`} data-testid="shared-route-segment" className="dc-route-shared"
              x1={segment.from[0] * VIEW_PPM} y1={segment.from[1] * VIEW_PPM}
              x2={segment.to[0] * VIEW_PPM} y2={segment.to[1] * VIEW_PPM}>
              <title>Общий сегмент с основным: {segment.edge_id}</title>
            </line>)}
          </g>}
          {showPlanning && <FloorPlanningLayer planning={planning} ppm={VIEW_PPM} zoom={tr.zoom}
            labels={(dc?.cable_trays_json as { planning_labels?: Record<string, string> } | undefined)?.planning_labels}
            racks={racks.map(rack => ({ ...placementFromSource(rack), id: rack.id, label: rackLabel(rack, dc) }))}
            onSelect={id => { const rack = racks.find(r => r.id === id); if (rack) onRackClick?.(rack) }} />}
          {workOrderOverlay && <g aria-label="Объекты наряда">
            <FloorPlanningLayer ppm={VIEW_PPM} zoom={tr.zoom}
              planning={{ placements: workOrderOverlay.moves.flatMap(move => move.rack_id && move.target && !move.conflict
                ? [{ rack_id: move.rack_id, target: move.target }] : []), rack_reservations: [] }}
              labels={Object.fromEntries(workOrderOverlay.moves.filter(move => move.target_label).map(move => [String(move.rack_id), move.target_label!]))}
              racks={racks.map(rack => ({ ...placementFromSource(rack), id: rack.id, label: rackLabel(rack, dc) }))}
              onSelect={id => { const rack = racks.find(candidate => candidate.id === id); if (rack) onRackClick?.(rack) }} />
            {workOrderOverlay.routes.filter(route => route.state === 'available' && route.route.length > 1).map((route, index) => <path
              key={route.cable_id ?? index} data-testid={`work-order-route-${index}`}
              d={`M ${route.route.map(point => `${point.x * VIEW_PPM},${point.y * VIEW_PPM}`).join(' L ')}`}
              fill="none" stroke={route.length_conflict ? '#ef4444' : '#fbbf24'} strokeWidth={3} vectorEffect="non-scaling-stroke">
              <title>U{route.route_endpoints?.source.u_slot} → U{route.route_endpoints?.target.u_slot}</title>
            </path>)}
          </g>}
          {!routeWorkbench && focusedRect && (() => {
            const label = rackLabel(focusedRect.rack, dc)
            const width = Math.max(64, label.length * 7 + 16)
            return <g data-testid="focused-rack-label" transform={`translate(${focusedRect.x + focusedRect.w / 2},${focusedRect.y - 8 / tr.zoom}) scale(${1 / tr.zoom})`} style={{ pointerEvents: 'none' }}>
              <rect x={-width / 2} y={-24} width={width} height={22} rx={3} fill="#0F172A" stroke="#38BDF8" strokeWidth={1} />
              <text textAnchor="middle" y={-9} fontSize={11} fontFamily="'JetBrains Mono',monospace" fill="#F8FAFC">{label}</text>
            </g>
          })()}
        </g>
        {routeWorkbench && <g data-plan-layer="labels" pointerEvents="none">
          {addressLabels.map(label => <g key={label.id} className="dc-rack-label" data-endpoint={label.endpoint}
            data-testid={label.endpoint ? `route-endpoint-${label.endpoint}` : `rack-address-${label.id}`}>
            {label.external && <line x1={label.anchorX} y1={label.anchorY}
              x2={Math.max(label.x, Math.min(label.x + label.w, label.anchorX))}
              y2={Math.max(label.y, Math.min(label.y + label.h, label.anchorY))} className="dc-label-leader" />}
            <rect x={label.x} y={label.y} width={label.w} height={label.h} rx={2} />
            <text x={label.x + label.w / 2} y={label.y + label.h / 2} textAnchor="middle" dominantBaseline="central">
              {label.bothEndpoints ? <tspan data-testid="route-endpoint-B">{label.label}</tspan> : label.label}
            </text>
          </g>)}
        </g>}
      </svg>

      {tooltip && (
        <div className="absolute z-50 pointer-events-none" style={{
          left: routeWorkbench ? Math.max(8, Math.min(tooltip.x, viewportSize.width - 190)) : tooltip.x,
          top: routeWorkbench ? Math.max(8, Math.min(tooltip.y, viewportSize.height - 136)) : tooltip.y,
        }}>
          <div className="bg-bg-panel border border-border rounded shadow-xl text-xs min-w-[148px]">
            <div className="px-2.5 py-1.5 border-b border-border font-mono font-bold text-text-primary text-[11px]">
              {tooltip.lbl}
            </div>
            <div className="px-2.5 py-1.5 flex flex-col gap-0.5 text-[10px] text-text-muted">
              {!tooltip.rack.is_facility && tooltip.rack.type !== 'pillar' && tooltip.rack.type !== 'column' && (tooltip.rack.address_confirmed === true
                ? <span>Ряд <span className="text-text-secondary">{tooltip.rack.row_index + 1}</span> · Стойка <span className="text-text-secondary">{tooltip.rack.rack_index + 1}</span></span>
                : <span className="text-status-warning">Место не подтверждено</span>)}
              {(() => {
                const tooltipTemp = getRackTemperature(tooltip.rack, telemetryMap)
                const tooltipThermal = getThermalColor(tooltipTemp)
                if (tooltipTemp == null) return null
                return (
                  <div className="flex items-center justify-between gap-2 pt-0.5 mt-0.5 border-t border-border/50 text-[10px]">
                    <span>Температура:</span>
                    <span
                      className="font-mono font-bold px-1 py-0.5 rounded text-[10px]"
                      style={{ color: tooltipThermal?.stroke ?? '#94a3b8', background: tooltipThermal?.badgeBg ?? 'transparent' }}
                    >
                      {tooltipTemp.toFixed(1)}°C
                    </span>
                  </div>
                )
              })()}
              {tooltip.rack.is_blocked && <span className="text-red-400">⊘ Заблокирована</span>}
              {tooltip.rack.id === srcId && <span className="text-yellow-400 font-semibold">▶ Источник (A)</span>}
              {tooltip.rack.id === tgtId && <span className="text-emerald-400 font-semibold">▶ Назначение (B)</span>}
            </div>
          </div>
        </div>
      )}

      <div className={'absolute bottom-3 right-3 flex flex-col gap-1.5 z-20 ' + (inspectorOpen ? 'lg:right-[432px] max-lg:hidden' : '')}>
        <label style={{ fontSize: 12, padding: 6, background: 'var(--bg-panel, #111827)', borderRadius: 4 }}>
          <input type="checkbox" aria-label="Планируемые размещения и резервы" checked={showPlanning} onChange={event => setShowPlanning(event.target.checked)} /> План / резервы
        </label>
        {(routeWorkbench || viewStateKey) && <div ref={layerControlsRef} className="dc-layer-controls relative w-11">
          <button type="button" title="Слои плана" aria-label="Слои плана" aria-expanded={layersOpen}
            className="w-11 h-11 flex items-center justify-center bg-bg-panel border border-border rounded text-text-secondary hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-accent"
            aria-controls={`${floorGridId}-layers`} onClick={() => setLayersOpen(open => !open)}><Layers size={17} /></button>
          {layersOpen && <fieldset id={`${floorGridId}-layers`} className="dc-layer-menu absolute bottom-0 right-14 min-w-0 bg-bg-panel border border-border rounded p-3 w-52 space-y-1" style={{ right: 56, width: 208 }}>
            <legend>Слои плана</legend>
            {([{ key: 'addresses', label: 'Адреса стоек' }, { key: 'floor', label: 'Плиты пола' }, { key: 'pathways', label: 'Лотки' }] as const).map(item =>
              <label key={item.key} className="flex items-center gap-2 min-h-11"><input type="checkbox" checked={layers[item.key]}
                onChange={event => setLayers(current => ({ ...current, [item.key]: event.target.checked }))} />{item.label}</label>)}
          </fieldset>}
        </div>}
        {[
          { Icon: Plus, title: 'Увеличить', testId: 'zoom-in', fn: () => zoomAt(viewportSize.width / 2, viewportSize.height / 2, 1.3) },
          { Icon: Minus, title: 'Уменьшить', testId: 'zoom-out', fn: () => zoomAt(viewportSize.width / 2, viewportSize.height / 2, 1 / 1.3) },
          ...(routeWorkbench && route.length ? [{ Icon: LocateFixed, title: 'Показать маршрут целиком', testId: 'zoom-route', fn: () => fitView(true) }] : []),
          ...(workOrderOverlay ? [{ Icon: LocateFixed, title: 'Показать весь наряд', testId: 'zoom-work-order', fn: () => fitView('work-order') }] : []),
          { Icon: Scan, title: 'Сбросить вид', testId: 'zoom-reset', fn: () => fitView() },
        ].map(({ Icon, title, testId, fn }) => (
          <button
            key={testId}
            data-testid={testId}
            onClick={fn}
            title={title}
            aria-label={title}
            className="w-11 h-11 min-w-[44px] min-h-[44px] flex items-center justify-center bg-bg-panel/95 border border-border rounded-md text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors cursor-pointer select-none"
          >
            <Icon size={17} />
          </button>
        ))}
      </div>

      {showPlanning && dc?.code && <FloorReservationsList code={dc.code} planning={planning} />}
      {routeWorkbench && <div className="dc-route-legend">
        <span><i data-kind="support" />Лотки</span>
        {route.length > 1 && <><span><i />Маршрут</span><span><i data-kind="entry" />Вводы стоек</span></>}
        {route.length === 1 && srcId === tgtId && <span>Внутри стойки</span>}
        {referenceRoute?.length && <span><i data-kind="reference" />Основной</span>}
        {sharedSegments.length > 0 && <span><i data-kind="shared" />Общие сегменты</span>}
      </div>}

      {routeWorkbench && <div className="dc-floor-key">
        <span className="dc-scale-bar"><i style={{ width: scaleM * VIEW_PPM * tr.zoom }} />{scaleM} м</span>
        <span><Grid3X3 size={14} />Плита {floor.tileSizeMm} × {floor.tileSizeMm} мм</span>
        {layers.addresses && addressLabels.length < visibleRects.length && <span>Адреса: {addressLabels.length}/{visibleRects.length}</span>}
      </div>}
      {!routeWorkbench && <div className="absolute bottom-3 left-3 flex flex-wrap items-center gap-3 text-[10px] text-text-muted pointer-events-none max-w-[calc(100%-120px)]">
        {(srcId != null || tgtId != null) && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border-2 border-yellow-400 bg-yellow-400/20"/>
              A — источник
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border-2 border-emerald-400 bg-emerald-400/20"/>
              B — назначение
            </span>
          </>
        )}
        {telemetryMap && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-xs" style={{ backgroundColor: 'rgba(2, 132, 199, 0.4)', border: '1px solid #0284c7' }}/>
              &lt;25°C
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-xs" style={{ backgroundColor: 'rgba(245, 158, 11, 0.4)', border: '1px solid #f59e0b' }}/>
              25–30°C
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-xs" style={{ backgroundColor: 'rgba(239, 68, 68, 0.5)', border: '1px solid #ef4444' }}/>
              &gt;30°C
            </span>
          </>
        )}
        {aisleRects.length > 0 && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border border-sky-400/60 bg-sky-400/20"/>
              Холодный
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm border border-red-400/60 bg-red-400/20"/>
              Горячий
            </span>
            {aisleRects.some(z => z.isContained) && (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm border border-dashed border-sky-400 bg-sky-400/20"/>
                Изоляция
              </span>
            )}
          </>
        )}
      </div>}

      <div className="absolute top-3 right-3 text-[11px] text-text-secondary font-mono pointer-events-none select-none" title="Масштаб схемы">
        {calculatorMapScaleLabel(tr.zoom)}
      </div>
    </div>
  )
}
